import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useParams, useSearchParams } from 'react-router'
import _ from 'lodash'

import { SpatialPolicy } from '@/models/spatialPolicy'
import { PolicyTypeId } from '@/models/spatialPolicyTypes'
import {
  useMobilityPolicies,
  usePolicies,
  usePolicyContext,
  useSaveMobilityPolicy,
} from '@/modules/policiesV1/hooks'
import { useLocationState } from '@/modules/urlRouting/hooks'
import { SearchParam } from '@/modules/urlRouting/types'
import { EditState } from '@/pages/mobility/policies/_policy-id/constants'

import { getFields } from './components/PolicyDetails/Form/policyTypeFieldValidations'
import { PolicyInfoModalProps } from './components/PolicyInfoModal'
import { getModalText } from './utils/modalText'
import { FormErrors, PolicyFormFieldName } from './utils/types'

/**
 * Mutator applied to a working-copy `SpatialPolicy`. Field components call this instead of
 * mutating the model in place — the reducer clones first, so each edit yields a fresh instance
 * and React re-renders (the model is no longer observable).
 */
export type EditPolicy = (mutate: (policy: SpatialPolicy) => void) => void

type EditAction =
  | { type: 'set'; policy: SpatialPolicy | undefined }
  | { type: 'edit'; mutate: (policy: SpatialPolicy) => void }

const policyEditReducer = (
  state: SpatialPolicy | undefined,
  action: EditAction
): SpatialPolicy | undefined => {
  switch (action.type) {
    case 'set':
      return action.policy
    case 'edit': {
      if (!state) return state
      const next = state.clone()
      action.mutate(next)
      return next
    }
  }
}

/**
 * Hook-based replacement for the deleted `PolicyEditStore`. Loads the working copy from the
 * list query (no per-policy fetch), drives edits through a clone-per-edit reducer (`editPolicy`),
 * saves via a React Query mutation, and keeps modal/validation state in `useState` — all exposed
 * through `PolicyEditContext` so the existing consumers keep their `store`-shaped interface
 * without MobX.
 */
export interface PolicyEditController {
  policy?: SpatialPolicy
  originalPolicy?: SpatialPolicy
  editPolicy: EditPolicy
  isNew: boolean
  isDuplicate: boolean
  isFetching: boolean
  isSaving: boolean
  policyNotFound: boolean
  savedPolicyId?: string
  formErrors: FormErrors
  setFormErrors: (value: FormErrors) => void
  clearFormErrors: () => void
  validatePolicy: () => FormErrors | undefined
  save: () => Promise<boolean>
  modalProps?: PolicyInfoModalProps
  setModalProps: (value?: PolicyInfoModalProps) => void
  clearModalProps: () => void
}

export const usePolicyEditController = ({
  editState,
}: {
  editState: EditState
}): PolicyEditController => {
  const { policyId } = useParams()
  const [searchParams] = useSearchParams()
  const { policyJson } = useLocationState()
  const { isLoading } = useMobilityPolicies()
  const policies = usePolicies()
  const context = usePolicyContext()
  const saveMutation = useSaveMobilityPolicy()

  const [policy, dispatch] = useReducer(policyEditReducer, undefined)
  const [originalPolicy, setOriginalPolicy] = useState<SpatialPolicy>()
  const [savedPolicyId, setSavedPolicyId] = useState<string>()
  const [formErrors, setFormErrorsState] = useState<FormErrors>({})
  const [modalProps, setModalPropsState] = useState<PolicyInfoModalProps>()
  const initializedRef = useRef(false)

  const isNew = editState === EditState.CREATE
  const isDuplicate = editState === EditState.DUPLICATE
  const sourceId = isDuplicate ? searchParams.get(SearchParam.POLICY_ID) : policyId

  const editPolicy = useCallback<EditPolicy>(mutate => dispatch({ type: 'edit', mutate }), [])

  const clearModalProps = useCallback(() => setModalPropsState(undefined), [])
  const setModalProps = useCallback(
    (value?: PolicyInfoModalProps) =>
      setModalPropsState(value ? { onConfirm: clearModalProps, ...value } : undefined),
    [clearModalProps]
  )

  const setFormErrors = useCallback(
    (value: FormErrors) => setFormErrorsState(prev => ({ ...prev, ...value })),
    []
  )
  const clearFormErrors = useCallback(() => setFormErrorsState({}), [])

  // The source policy the working copy is initialized from (edit/duplicate). Resolved from the
  // list query, falling back to the location-state preview while it loads (the just-viewed policy).
  const sourcePolicy = useMemo(() => {
    if (isNew) return undefined
    const fromList = policies.find(({ constantPolicyUUID }) => constantPolicyUUID === sourceId)
    if (fromList) return fromList
    return policyJson ? new SpatialPolicy(policyJson, context) : undefined
  }, [isNew, policies, sourceId, policyJson, context])

  // Initialize the working copy exactly once. Clones the source so edits never mutate the
  // RQ-cached model; for create, builds a fresh policy from the search params.
  useEffect(() => {
    if (initializedRef.current) return
    if (isNew) {
      const created = new SpatialPolicy(undefined, context)
      const policyName = searchParams.get(SearchParam.POLICY_NAME)
      const policyType = searchParams.get(SearchParam.POLICY_TYPE)
      if (policyName) created.setPolicyName(policyName)
      if (policyType) created.setPolicyType(policyType as PolicyTypeId)
      dispatch({ type: 'set', policy: created })
      initializedRef.current = true
      return
    }
    if (sourcePolicy) {
      const working = sourcePolicy.clone()
      if (isDuplicate) working.setAsDuplicate()
      dispatch({ type: 'set', policy: working })
      setOriginalPolicy(sourcePolicy.clone())
      initializedRef.current = true
    }
  }, [isNew, isDuplicate, sourcePolicy, searchParams, context])

  const validatePolicy = useCallback(() => {
    if (!policy || !policy.policyType) return
    const fields = getFields(policy.policyType)
    let newErrors = {} as FormErrors
    Object.keys(fields).forEach(field => {
      const validator = field in fields && fields[field as PolicyFormFieldName]?.validator
      const error = validator && validator(policy)
      newErrors = { ...newErrors, [field]: error || undefined }
    })
    setFormErrors(newErrors)
    return newErrors
  }, [policy, setFormErrors])

  const save = useCallback(async (): Promise<boolean> => {
    if (!policy) {
      setModalProps({ ...getModalText().ERROR_MISSING_POLICY })
      return false
    }
    const validatedErrors = validatePolicy()
    // if invalid fields don't bother calling API
    if (!_.values(validatedErrors).every(_.isEmpty)) {
      const errorFields = _.keys(_.pickBy(validatedErrors))
      console.error(`Invalid fields ${errorFields.join(', ')}`)
      return false
    }
    try {
      const response = await saveMutation.mutateAsync(policy.toJson)
      const newPolicy = new SpatialPolicy(response.plcy, context)
      setOriginalPolicy(newPolicy)
      setSavedPolicyId(newPolicy?.constantPolicyUUID)
      return true
    } catch {
      setModalProps({ ...getModalText().ERROR_SAVE })
      return false
    }
  }, [policy, saveMutation, setModalProps, validatePolicy, context])

  const policyNotFound = !isNew && (!sourceId || (!isLoading && !sourcePolicy))

  return {
    policy,
    originalPolicy,
    editPolicy,
    isNew,
    isDuplicate,
    isFetching: !isNew && isLoading,
    isSaving: saveMutation.isPending,
    policyNotFound,
    savedPolicyId,
    formErrors,
    setFormErrors,
    clearFormErrors,
    validatePolicy,
    save,
    modalProps,
    setModalProps,
    clearModalProps,
  }
}

export const PolicyEditContext = createContext<PolicyEditController | undefined>(undefined)

export const usePolicyEditContext = () => {
  const context = useContext(PolicyEditContext)
  if (context === undefined) {
    throw new Error('usePolicyEditContext must be used within PolicyEditContext.Provider')
  }
  return context
}
